import {RoutingRulesProps} from './route.types';

/**
 * Framework-agnostic routing-rule matching. These pure functions are shared by
 * the client router ({@link ./routing.ts}) and the server-side sitemap filter
 * ({@link ../../server/service/RoutingRulesService}) so the "which app owns this
 * path" logic lives in exactly one place.
 */

/**
 * Return the first routing rule whose regex matches `pathname`, or undefined.
 * Rules are evaluated in order, so ordering in the source list is significant.
 * A rule with no `rule` string is skipped. An invalid regex is treated as a
 * non-match rather than throwing.
 */
export const getFirstMatch = (
    routingRules: RoutingRulesProps[],
    pathname: string
): RoutingRulesProps | undefined => {
    if (!Array.isArray(routingRules)) {
        return undefined;
    }
    for (const item of routingRules) {
        if (item && item.rule) {
            try {
                if (new RegExp(item.rule).test(pathname)) {
                    return item;
                }
            } catch {
                // Malformed regex in the rules payload — skip it, don't crash.
            }
        }
    }
    return undefined;
};

/**
 * True when `matchOrigin` names an app other than this one (`selfOrigin`).
 * A falsy match origin (no rule matched, or a rule without an origin) is treated
 * as "this app", so it is never considered another app's route.
 */
export const isOtherOrigin = (
    matchOrigin: string | undefined,
    selfOrigin: string | undefined
): boolean => Boolean(matchOrigin && matchOrigin !== selfOrigin);

/**
 * True when `pathname` is owned by an app whose origin differs from `selfOrigin`
 * according to the routing rules. Combines {@link getFirstMatch} and
 * {@link isOtherOrigin}.
 */
export const isOtherAppPath = (
    routingRules: RoutingRulesProps[],
    pathname: string,
    selfOrigin: string | undefined
): boolean => isOtherOrigin(getFirstMatch(routingRules, pathname)?.origin, selfOrigin);

/** A routing rule with its regex compiled once, ready to test many paths. */
export interface CompiledRoutingRule {
    re: RegExp;
    origin: string;
}

/**
 * Compile each rule's regex a single time. Rules without a `rule` string and
 * rules with a malformed regex are dropped (a malformed regex is skipped rather
 * than throwing, matching {@link getFirstMatch}). Order is preserved so callers
 * still see first-match-wins semantics.
 *
 * Use this when testing many paths against the same rule set (e.g. filtering a
 * large page list) so each regex is compiled once instead of once per path.
 */
export const compileRoutingRules = (
    routingRules: RoutingRulesProps[]
): CompiledRoutingRule[] => {
    if (!Array.isArray(routingRules)) {
        return [];
    }
    const compiled: CompiledRoutingRule[] = [];
    for (const item of routingRules) {
        if (item && item.rule) {
            try {
                compiled.push({re: new RegExp(item.rule), origin: item.origin});
            } catch {
                // Malformed regex in the rules payload — skip it, don't crash.
            }
        }
    }
    return compiled;
};

/** Return the first compiled rule whose regex matches `pathname`, or undefined. */
export const firstCompiledMatch = (
    compiled: CompiledRoutingRule[],
    pathname: string
): CompiledRoutingRule | undefined => compiled.find((rule) => rule.re.test(pathname));

/**
 * Compiled-rule equivalent of {@link isOtherAppPath}: true when `pathname` is
 * owned by an app whose origin differs from `selfOrigin`.
 */
export const isOtherAppPathCompiled = (
    compiled: CompiledRoutingRule[],
    pathname: string,
    selfOrigin: string | undefined
): boolean => isOtherOrigin(firstCompiledMatch(compiled, pathname)?.origin, selfOrigin);
